/**
 * After a review card saves, the fields the server stored in a different spelling
 * than the owner typed, so the page can say so instead of changing the owner's
 * text silently. The server says which rule decided each value
 * (analysis/taxonomy-values.ts): `existing` — a value already in use matched, so
 * it was stored in that spelling; `standard` — nothing in use matched, but the
 * color synonym dictionary respelled it ("grey" → "gray"). A difference in spacing
 * alone is not reported. Unit-tested in saved-as.test.ts.
 */

export type SavedRule = 'existing' | 'standard' | 'typed';

export interface SavedAsField {
  label: string;
  typed: string | null | undefined;
  saved: string | null | undefined;
  /** The rule that chose the stored value; null or absent when none was reported. */
  rule?: SavedRule | null | undefined;
}

function clean(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

/** One sentence per field whose stored value differs from what was typed. */
export function savedAsNotes(fields: readonly SavedAsField[]): string[] {
  const notes: string[] = [];
  for (const f of fields) {
    const typed = clean(f.typed);
    const saved = clean(f.saved);
    if (typed === '' || saved === '' || typed === saved) continue;
    const why = f.rule === 'standard' ? 'the standard spelling' : 'the spelling already in use';
    notes.push(`${f.label} saved as “${saved}”, ${why}.`);
  }
  return notes;
}
